import { useLoaderData, useNavigate, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Card, Icon, KpiCard, Progress } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import { Select } from '@ds/forms';
import { SegmentedControl, Tabs } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples, moeda } from '@/comuns/formato';
import { dados } from '@/data/porta';
import type {
    AbaDeRelatorios, CargaDeRelatorios, LinhaDeRanking, LinhaDoDiaDaSemana, PeriodoDoRelatorio,
} from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { useRotaAtual } from '@/router/useRotaAtual';
import { mesCurto } from './financeiro/Relatorios';

const ABAS: AbaDeRelatorios[] = ['operacional', 'financeiro', 'clinico', 'comercial'];
const PERIODOS: PeriodoDoRelatorio[] = ['semana', 'mes', 'ano'];

function abaDaRota(pathname: string): AbaDeRelatorios {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeRelatorios) : 'operacional';
}

export async function carregarRelatorios({ request }: LoaderFunctionArgs): Promise<CargaDeRelatorios> {
    const url = new URL(request.url);
    const periodo = url.searchParams.get('periodo') as PeriodoDoRelatorio | null;
    return dados().relatorios({
        aba: abaDaRota(url.pathname),
        periodo: periodo && PERIODOS.includes(periodo) ? periodo : 'mes',
        profissional: url.searchParams.get('profissional') ?? undefined,
    });
}

export function Relatorios() {
    const carga = useLoaderData() as CargaDeRelatorios;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const handle = useRotaAtual();

    const rota = rotaPorId('relatorios');
    /* Zero horas abertas é jornada não declarada, e não clínica que fecha a
       semana inteira: o que muda é o que a tela pode afirmar. */
    const comJornada = carga.horasAbertas > 0;
    const aba = carga.aba;
    const subrota = handle?.subrota?.path ?? 'operacional';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            return proximos;
        }, { replace: true });
    };

    const colunasDoRanking: DataTableColumn<LinhaDeRanking>[] = [
        { key: 'procedimento', header: 'Procedimento', sortable: true, render: (l) => <strong style={{ fontWeight: 600 }}>{l.procedimento}</strong> },
        { key: 'quantidade', header: 'Realizados', align: 'right' },
        { key: 'ticketCentavos', header: 'Ticket médio', align: 'right', render: (l) => moeda(l.ticketCentavos) },
        { key: 'receitaCentavos', header: 'Receita', align: 'right', render: (l) => <strong style={{ fontWeight: 600 }}>{moeda(l.receitaCentavos)}</strong> },
        {
            key: 'participacao', header: 'Participação', align: 'right',
            render: (l) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                    <Progress value={l.participacao} style={{ flex: 1 }} size="sm" ariaLabel={`Participação de ${l.procedimento}`} />
                    <span style={{ width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.participacao}%</span>
                </span>
            ),
        },
    ];

    const maiorMes = Math.max(1, ...carga.serie.map((p) => p.valorCentavos));
    const periodoAtual = (params.get('periodo') as PeriodoDoRelatorio | null) ?? 'mes';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Relatórios"
                description={`Desempenho da clínica · ${dataSimples(carga.inicio)} a ${dataSimples(carga.fim)}`}
                actions={
                    <ExportMenu<LinhaDeRanking>
                        label="Exportar" size="md"
                        payload={{
                            title: 'Relatório — procedimentos',
                            subtitle: `${dataSimples(carga.inicio)} a ${dataSimples(carga.fim)}`,
                            rows: carga.ranking,
                            columns: [
                                { header: 'Procedimento', value: (l) => l.procedimento },
                                { header: 'Realizados', value: (l) => String(l.quantidade) },
                                { header: 'Ticket médio', value: (l) => moeda(l.ticketCentavos) },
                                { header: 'Receita', value: (l) => moeda(l.receitaCentavos) },
                                { header: 'Participação', value: (l) => `${l.participacao}%` },
                            ],
                        }}
                    />
                }
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ flex: '1 1 240px', minWidth: 0 }}>
                        <Tabs
                            ariaLabel="Recortes do relatório" activeId={subrota}
                            onChange={(id) => navigate({ pathname: `/app/relatorios/${id}`, search: params.toString() ? `?${params}` : '' })}
                            tabs={(rota?.children ?? []).map((c) => ({ id: c.path, label: c.label }))}
                        />
                    </span>
                    <Select
                        value={carga.profissional ?? ''} size="sm" style={{ width: 200 }}
                        aria-label="Filtrar por profissional"
                        onChange={(v) => trocarParam('profissional', v || null)}
                        options={[{ value: '', label: 'Todos os profissionais' }].concat(carga.profissionais.map((p) => ({ value: p, label: p })))}
                    />
                    <SegmentedControl
                        value={periodoAtual} size="sm" ariaLabel="Período do relatório"
                        onChange={(v) => trocarParam('periodo', v === 'mes' ? null : v)}
                        options={[{ value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mês' }, { value: 'ano', label: 'Ano' }]}
                    />
                </div>
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard
                        key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio}
                        delta={i.variacao} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />}
                    />
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'var(--layout-colunas, minmax(0,1.5fr) minmax(0,1fr))', gap: 16, alignItems: 'start' }}>
                <Card title="Receita recebida por mês" subtitle="Últimos doze meses, líquido de taxa" padding={18}>
                    {/* O gráfico é ilustração; o número está nos indicadores e na
                        tabela — por isso ele sai inteiro da árvore de acessibilidade. */}
                    <div role="img" aria-label="Receita recebida mês a mês nos últimos doze meses" style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 150 }}>
                        {carga.serie.map((ponto, indice) => (
                            <span key={ponto.mes} aria-hidden="true" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, justifyContent: 'flex-end', height: '100%' }}>
                                <span
                                    style={{
                                        width: '100%', maxWidth: 30,
                                        height: `${Math.max(ponto.valorCentavos === 0 ? 2 : 4, Math.round((ponto.valorCentavos / maiorMes) * 100))}%`,
                                        borderRadius: '4px 4px 0 0',
                                        background: indice === carga.serie.length - 1 ? 'var(--burgundy-700)' : 'var(--burgundy-100)',
                                    }}
                                />
                                <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{mesCurto(ponto.mes).split('/')[0]}</span>
                            </span>
                        ))}
                    </div>
                </Card>

                <Card
                    title="Agenda por dia da semana"
                    subtitle={comJornada ? 'Horas agendadas sobre horas abertas' : 'Horas agendadas no período'}
                    padding={18}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                        {carga.porDia.map((linha: LinhaDoDiaDaSemana) => {
                            /* Com jornada, a barra é ocupação de verdade; sem
                               ela, continua sendo comparação entre dias — e o
                               rodapé diz qual das duas está à vista. */
                            const valor = linha.ocupacao ?? linha.participacao;
                            return (
                                /* Larguras mínimas, não fixas: no celular o número desce
                                   para baixo da barra em vez de passar da borda do card. */
                                <div key={linha.dia} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px 12px' }}>
                                    <span style={{ flex: '0 0 66px', fontSize: 13 }}>{linha.dia}</span>
                                    <Progress
                                        value={Math.min(100, valor)} tone={valor < 50 ? 'warning' : 'brand'} style={{ flex: '1 1 60px' }}
                                        ariaLabel={comJornada ? `Ocupação de ${linha.dia}` : `Horas de ${linha.dia}`}
                                    />
                                    <span style={{ flex: '0 0 auto', minWidth: 72, marginLeft: 'auto', whiteSpace: 'nowrap', fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>
                                        {comJornada ? `${valor}% · ${linha.horas} h` : `${linha.horas} h`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <Alert tone="neutral" dismissible={false} style={{ marginTop: 14 }}>
                        {comJornada ? (
                            <>
                                A barra é a <strong>ocupação</strong>: horas agendadas sobre as horas em que a
                                clínica abre, segundo a jornada de Configurações › Agenda. Acima de 100% houve
                                atendimento fora do expediente declarado.
                            </>
                        ) : (
                            <>
                                A barra compara os dias <strong>entre si</strong>, tomando o mais cheio como 100%.
                                Taxa de ocupação exige a jornada de trabalho — declare os horários em
                                Configurações › Agenda.
                            </>
                        )}
                    </Alert>
                </Card>
            </div>

            {aba === 'clinico' && (
                <Card title="Atendimentos por profissional" subtitle="Concluídos no período" padding={18}>
                    {carga.clinico.porProfissional.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhum atendimento concluído no período.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {carga.clinico.porProfissional.map((p) => (
                                <div key={p.profissional} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                                    <span style={{ flex: 1 }}>{p.profissional}</span>
                                    <Badge tone="neutral">{p.quantidade} {p.quantidade === 1 ? 'atendimento' : 'atendimentos'}</Badge>
                                    <span style={{ width: 60, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{p.horas} h</span>
                                </div>
                            ))}
                            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                {carga.clinico.faltas} {carga.clinico.faltas === 1 ? 'falta' : 'faltas'} e{' '}
                                {carga.clinico.cancelados} {carga.clinico.cancelados === 1 ? 'cancelamento' : 'cancelamentos'} no período.
                            </p>
                        </div>
                    )}
                </Card>
            )}

            {aba === 'comercial' && (
                <Card title="Leads por origem" subtitle="Criados no período" padding={18}>
                    {carga.comercial.porOrigem.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhum lead criado no período.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {carga.comercial.porOrigem.map((o) => (
                                <div key={o.origem} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                                    <span style={{ width: 120 }}>{o.origem}</span>
                                    <Progress
                                        value={Math.round((o.quantidade / Math.max(1, carga.comercial.leads)) * 100)} style={{ flex: 1 }}
                                        ariaLabel={`Leads de ${o.origem}`}
                                    />
                                    <span style={{ width: 30, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{o.quantidade}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            )}

            <Card title="Procedimentos por receita" subtitle={`${dataSimples(carga.inicio)} a ${dataSimples(carga.fim)} · atendimentos concluídos`} padding={0}>
                <DataTable
                    columns={colunasDoRanking} rows={carga.ranking} rowKey="procedimento"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum atendimento concluído no período."
                />
            </Card>
        </div>
    );
}
